import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getHistoricalLogisticsOrder } from "../../../../config/Services";
import { useSnackbar } from "../../product/hooks/SnackbarProvider";
import OrderDetailsModal from "./OrderDetailsModal";
import { getStoreCurrencySymbol } from "../../../../utils/currency";

const MapPinIcon = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const getTypeBadge = (type) => {
  const t = (type || "").toUpperCase();
  if (t === "DELIVERED") return { bg: "#d1fae5", color: "#047857", label: "Delivered" };
  if (t === "CANCELLED") return { bg: "#ffe4e6", color: "#be123c", label: "Cancelled" };
  if (t === "PENDING") return { bg: "#fef3c7", color: "#b45309", label: "Pending" };
  if (t === "SHIPPED") return { bg: "#dbeafe", color: "#1d4ed8", label: "Shipped" };
  return { bg: "#f1f5f9", color: "#475569", label: type || "N/A" };
};

export default function DeliveryHistory({
  searchParams,
  setSearchParams,
  showSnackbar,
  orgId,
  locId,
  debouncedSearchValue,
}) {
  const initialPage = Number(searchParams.get("page")) || 0;
  const snackbar = useSnackbar();

  const [selectedShipmentId, setSelectedShipmentId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (shipmentId) => {
    setSelectedShipmentId(shipmentId);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedShipmentId(null);
  };

  const comingSoonAlert = (e) => {
    e.stopPropagation();
    snackbar("Coming Soon", "warning");
  };

  const [pageState, setPageState] = useState({
    page: initialPage,
    pageSize: 10,
    totalResults: 0,
    totalPages: 0,
    data: [],
    loader: false,
  });

  const handlePageChange = (newPage) => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", newPage);
    setSearchParams(updatedParams);
    getHistoricalOrders(newPage);
  };

  const getHistoricalOrders = async (page) => {
    try {
      setPageState((prev) => ({ ...prev, loader: true }));

      const payload = {
        startDate: "",
        endDate: "",
        locationId: locId,
        organizationId: orgId,
        pageNumber: page,
        pageSize: pageState?.pageSize,
      };

      const response = await getHistoricalLogisticsOrder(payload);

      if (response?.data?.data?.es) {
        showSnackbar("Something went wrong", "error");
        return;
      }

      const mappedData = response?.data?.data?.orders?.map((item) => ({
        ...item,
        date: item?.pickupTime
          ? moment.utc(item?.pickupTime).local().format("D MMM YYYY, hh:mm A")
          : "NA",
        shipmentId: item?.omsOrderId || "N/A",
        type: item?.logisticsOrderStatus || "N/A",
        provider: item?.logisticsProvider || "N/A",
        deliveryCost: item?.deliveryCharge != null ? `${getStoreCurrencySymbol()}${item.deliveryCharge}` : "N/A",
      }));

      setPageState((prev) => ({
        ...prev,
        page,
        data: mappedData || [],
        totalResults: response?.data?.data?.totalOrders || 0,
        totalPages: response?.data?.data?.totalPages || 0,
        loader: false,
      }));
    } catch (err) {
      setPageState((prev) => ({ ...prev, loader: false, data: [] }));
    }
  };

  useEffect(() => {
    getHistoricalOrders(initialPage);
  }, [debouncedSearchValue]);

  const currentPage = Number(pageState.page);

  return (
    <div className="dh___wrapper">
      {/* ── Grid card ── */}
      <div className="dh___card">
        {/* Header row */}
        <div className="dh___row dh___header-row">
          <div className="dh___cell dh___header-cell">Date</div>
          <div className="dh___cell dh___header-cell">Shipment ID</div>
          <div className="dh___cell dh___header-cell">Type</div>
          <div className="dh___cell dh___header-cell">Provider</div>
          <div className="dh___cell dh___header-cell">Route</div>
          <div className="dh___cell dh___header-cell dh___cell-right">Delivery Cost</div>
          <div className="dh___cell dh___header-cell" />
        </div>

        {/* Loading */}
        {pageState?.loader && (
          <div className="dh___state-row">
            <p className="dh___state-text">Loading…</p>
          </div>
        )}

        {/* Empty */}
        {!pageState?.loader && pageState?.data?.length === 0 && (
          <div className="dh___state-row">
            <p className="dh___state-text">No delivery history found.</p>
          </div>
        )}

        {/* Data rows */}
        {!pageState?.loader &&
          pageState?.data?.map((row, idx) => {
            const badge = getTypeBadge(row?.type);
            const pickup = row?.pickupLocationName || row?.pickupLocation || row?.pickup || "N/A";
            const drop = row?.dropLocationName || row?.dropLocation || row?.drop || "N/A";
            const distance = row?.distance ? `(${row?.distance}km)` : "";

            return (
              <div key={row?.shipmentId || idx} className="dh___row dh___data-row">
                {/* Date */}
                <div className="dh___cell dh___cell-date">{row?.date}</div>

                {/* Shipment ID */}
                <div className="dh___cell dh___cell-id">{row?.shipmentId}</div>

                {/* Type badge */}
                <div className="dh___cell">
                  <span
                    className="dh___badge"
                    style={{ background: badge?.bg, color: badge?.color }}
                  >
                    {badge?.label}
                  </span>
                </div>

                {/* Provider */}
                <div className="dh___cell dh___cell-provider">{row?.provider}</div>

                {/* Route */}
                <div className="dh___cell">
                  <div className="dh___route">
                    <MapPinIcon color="#10b981" />
                    <span className="dh___route-location" title={pickup}>
                      {pickup}
                    </span>
                    <span className="dh___route-arrow">→</span>
                    <MapPinIcon color="#f43f5e" />
                    <span className="dh___route-location" title={drop}>
                      {drop}
                    </span>
                    {distance && <span className="dh___route-distance">{distance}</span>}
                  </div>
                </div>

                {/* Delivery Cost */}
                <div className="dh___cell dh___cell-right dh___cell-cost">{row?.deliveryCost}</div>

                {/* Chevron */}
                <div className="dh___cell dh___cell-center">
                  <button className="dh___chevron-btn" onClick={() => openModal(row?.shipmentId)}>
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {/* ── Pagination ── */}
      {pageState.totalPages > 1 && (
        <div className="dh___pagination">
          <button
            className="dh___page-btn"
            disabled={currentPage <= 0}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            ← Previous
          </button>
          <span className="dh___page-info">
            Page {currentPage + 1} of {pageState?.totalPages}
          </span>
          <button
            className="dh___page-btn"
            disabled={currentPage >= pageState?.totalPages - 1}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Next →
          </button>
        </div>
      )}

      {/* ── Shipment detail drawer ── */}
      <OrderDetailsModal
        open={modalOpen}
        onClose={closeModal}
        shipmentId={selectedShipmentId}
        showSnackbar={snackbar}
        comingSoonAlert={comingSoonAlert}
      />
    </div>
  );
}
